import type { LearnTopic } from "../topic-types";

/**
 * Dissociation.
 *
 * Educational, trauma-informed content about dissociation as a protective
 * response that exists on a spectrum. This is not a diagnostic tool.
 */
export const DISSOCIATION_TOPIC: LearnTopic = {
  slug: "dissociation",
  emoji: "🌫️",
  title: "Understanding Dissociation",
  tagline: "When the mind creates distance",
  theme: "sky",
  category: "cptsd-responses",
  isNew: true,
  seoDescription:
    "A trauma-informed, plain-English guide to dissociation — feeling disconnected, numbness, depersonalisation, derealisation and shutdown — with grounding practices and a printable early-signs worksheet from Recovery Trauma™.",
  cardBlurb: "What dissociation is, how to recognise it early, and how to gently reconnect.",
  intro: [
    "Dissociation is a natural, protective response that creates distance between you and something that feels too much to feel, think about, or process all at once. It can range from ordinary everyday moments — like driving somewhere and not remembering the journey — through to more persistent experiences of feeling disconnected from your body, your surroundings, or your sense of self.",
    "For many people who have lived through difficult or overwhelming experiences, dissociation became a way the mind and body found to cope when there was no other escape available. It is not a weakness, a character flaw, or something to be ashamed of — it is an intelligent, if costly, form of protection.",
    "This guide is educational only. It can help you recognise dissociation, understand why it happens, and learn some gentle ways of reconnecting when it feels safe to do so. It is not a diagnostic tool, and it cannot tell you whether you have a dissociative disorder — only a qualified professional can assess that.",
  ],
  closing:
    "Dissociation exists on a spectrum, and noticing yourself somewhere on it is not the same as being diagnosed with anything. If dissociation is frequent, distressing, or affecting your daily life, please consider speaking with a doctor or a trauma-informed therapist who can properly assess what is happening for you. If you ever feel unsafe, our Crisis Resources page is always here.",
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What dissociation is, and why it exists on a spectrum.",
      intro:
        "Plain-English education first. Nothing here asks you to notice anything in yourself yet — you can simply read.",
      resources: [
        {
          slug: "what-is-dissociation",
          kind: "learn",
          title: "What is dissociation?",
          summary: "A protective response that creates distance from overwhelm.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Dissociation is a way the mind protects itself by separating thoughts, feelings, memories, sensations or sense of identity from ordinary awareness. When something is too overwhelming to process in the moment, the mind can create distance from it instead — a kind of internal step back.",
            },
            {
              type: "list",
              title: "Some ideas people find helpful",
              items: [
                "Dissociation is not rare or unusual — almost everyone experiences mild versions of it",
                "It exists on a spectrum, from everyday zoning out to more persistent disconnection",
                "It often develops because it worked, at some point, to make something bearable",
                "It is not something to be ashamed of, even when it feels confusing from the outside",
              ],
            },
            {
              type: "note",
              text: "This page is educational only. It is not a screening tool, and it cannot diagnose a dissociative disorder — only a qualified professional can do that, through proper assessment.",
            },
          ],
        },
        {
          slug: "the-spectrum",
          kind: "learn",
          title: "The spectrum of dissociation",
          summary: "From everyday zoning out to more persistent disconnection.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Dissociation is not one single experience — it covers a wide range, and most people move along this spectrum at different times depending on stress, safety and history.",
            },
            {
              type: "flow",
              title: "A rough spectrum",
              items: [
                "Everyday: daydreaming, driving on 'autopilot', losing track of time while absorbed",
                "Mild: feeling a bit foggy, distant, or 'not quite there' during stress",
                "Moderate: emotional numbness, feeling detached from your body or surroundings",
                "More persistent: depersonalisation, derealisation, gaps in memory, shutdown",
              ],
            },
            {
              type: "prose",
              text: "Everyone's position on this spectrum can shift, sometimes within the same day. Recognising where you tend to sit is about self-understanding, not labelling.",
            },
          ],
        },
        {
          slug: "why-it-develops",
          kind: "learn",
          title: "Why dissociation develops",
          summary: "Protection when fight, flight or fawn are not possible.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "When a person cannot fight, flee, or change a frightening or overwhelming situation, the mind can find another way to survive it: creating distance. This is especially common when overwhelming experiences happen repeatedly, or in childhood, when there is little power to change or leave a situation.",
            },
            {
              type: "steps",
              title: "A common pattern",
              items: [
                { label: "Something feels unbearable", note: "And there is no way to stop or escape it." },
                { label: "The mind creates distance", note: "From the feeling, the body, or the moment itself." },
                { label: "It works", note: "The overwhelming feeling becomes more bearable, or disappears." },
                { label: "The pattern can generalise", note: "So it starts showing up in situations that are not actually dangerous." },
              ],
            },
            {
              type: "note",
              text: "If dissociation shows up often in your life now, it may be because it once did an important job. That does not mean it is still needed in the same way — but it usually deserves understanding rather than force.",
            },
          ],
        },
        {
          slug: "emotional-numbness",
          kind: "learn",
          title: "Emotional numbness",
          summary: "When feelings seem muted, distant, or absent altogether.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Emotional numbness is one of the more common forms of dissociation. It can feel like watching your own life from behind glass — you know something should feel a certain way, but the feeling itself seems muted or missing.",
            },
            {
              type: "list",
              title: "This can look like",
              items: [
                "Difficulty feeling joy, sadness or excitement, even at significant moments",
                "Going through the motions of daily life without much felt sense of it",
                "Struggling to answer 'how do you feel?' because nothing comes up clearly",
                "Feeling flat or 'grey' for stretches of time",
              ],
            },
            {
              type: "prose",
              text: "Numbness is not the absence of feeling — it is usually feeling turned down, as protection from something that would otherwise be too intense.",
            },
          ],
        },
        {
          slug: "depersonalisation",
          kind: "learn",
          title: "Depersonalisation",
          summary: "Feeling disconnected from your own body, thoughts or self.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Depersonalisation is a sense of being disconnected from yourself — your body, your thoughts, your emotions, or your sense of identity. People sometimes describe it as feeling unreal, like an observer of their own life, or as though they are on autopilot and someone else is driving.",
            },
            {
              type: "list",
              title: "People sometimes describe",
              items: [
                "Feeling like they are watching themselves from outside their body",
                "Their voice or hands feeling unfamiliar or not quite theirs",
                "A sense of emotional distance from their own thoughts",
                "Feeling like they are 'going through the motions' rather than living",
              ],
            },
            {
              type: "note",
              text: "Occasional depersonalisation is common and not usually a cause for alarm. If it is frequent, distressing, or lasts a long time, it is worth discussing with a doctor or therapist.",
            },
          ],
        },
        {
          slug: "derealisation",
          kind: "learn",
          title: "Derealisation",
          summary: "When the world itself feels distant, unreal or dreamlike.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Where depersonalisation affects the sense of self, derealisation affects the sense of the outside world. Surroundings can feel foggy, dreamlike, distant, or somehow artificial — as though there is a pane of glass between you and everything around you.",
            },
            {
              type: "list",
              title: "This can include",
              items: [
                "Familiar places suddenly feeling unfamiliar or 'off'",
                "Sounds or colours seeming muffled, flat, or distorted",
                "A sense that other people are behaving like actors, or that nothing is quite real",
                "Time feeling like it is moving strangely — too fast, too slow, or disjointed",
              ],
            },
            {
              type: "prose",
              text: "Derealisation can be unsettling, but it is a recognised response and not a sign that something is fundamentally wrong with you. It usually eases as safety and grounding return.",
            },
          ],
        },
        {
          slug: "shutdown",
          kind: "learn",
          title: "Shutdown and freeze",
          summary: "When the body goes still, heavy, or hard to move.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Shutdown is a deeper form of the freeze response, where the body conserves energy by slowing right down. It can feel like heaviness, exhaustion, an inability to speak or move, or a kind of collapse — sometimes alongside numbness or a sense of unreality.",
            },
            {
              type: "list",
              title: "Signs of shutdown",
              items: [
                "Heaviness or leadenness in the limbs",
                "Difficulty finding words or forming sentences",
                "A drop in energy that feels sudden rather than gradual tiredness",
                "Wanting to withdraw, go quiet, or disappear",
              ],
            },
            {
              type: "note",
              text: "Shutdown is a survival response, not laziness or giving up. Gentle, patient support — for yourself or someone else — tends to help far more than pressure to 'snap out of it'.",
            },
          ],
        },
        {
          slug: "memory-difficulties",
          kind: "learn",
          title: "Memory difficulties",
          summary: "Gaps, fog, or trouble recalling parts of an experience.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Dissociation can affect memory in various ways — from ordinary forgetfulness during a stressful day, to noticeable gaps around distressing events, to a general sense of foggy or patchy recall of periods of life.",
            },
            {
              type: "list",
              title: "This might include",
              items: [
                "Losing time — arriving somewhere with little memory of getting there",
                "Gaps around a specific stressful or frightening event",
                "A general sense of foggy memory during certain periods of your life",
                "Finding it hard to recall recent conversations or decisions",
              ],
            },
            {
              type: "note",
              text: "Memory difficulties can have many causes, including stress, sleep, trauma, or unrelated medical reasons. If they concern you, it's worth speaking to a GP as a first step.",
            },
          ],
        },
        {
          slug: "nervous-system-and-dissociation",
          kind: "learn",
          title: "Dissociation and the nervous system",
          summary: "How disconnection can form part of an automatic protective response.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Dissociation can accompany freeze, shutdown or overwhelming activation. Attention narrows and connection to sensation, emotion or surroundings may reduce when the nervous system predicts that full awareness would be too much. This is one possible framework, not a complete explanation for every experience.",
            },
            {
              type: "cta",
              text: "Explore activation, shutdown and flexible regulation in more depth.",
              label: "Trauma & the Nervous System",
              to: "/learn/trauma-nervous-system",
            },
          ],
        },
        {
          slug: "dissociative-amnesia-and-uncertainty",
          kind: "learn",
          title: "Dissociative amnesia, memory and uncertainty",
          summary: "Taking memory gaps seriously without filling them in or forcing recall.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Dissociative amnesia is a clinical term used when important autobiographical information cannot be recalled in a way that is not explained by ordinary forgetting. Memory can also be affected by stress, sleep, substances, medication, neurological conditions and many other factors.",
            },
            {
              type: "note",
              text: "A sensation, image or gap is not proof of a hidden event. Avoid methods that promise to recover accurate memories. A qualified professional can assess concerns while respecting uncertainty and avoiding suggestion.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognise",
      blurb: "Gently noticing early signs, without labelling or alarm.",
      intro:
        "Recognising your own patterns can bring a sense of relief and understanding. This is not a diagnostic checklist — it is a starting point for self-awareness.",
      resources: [
        {
          slug: "early-signs",
          kind: "learn",
          title: "Recognising early signs",
          summary: "The small cues that often arrive before dissociation deepens.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Dissociation often has an early stage, before it deepens into numbness or disconnection. Learning your own early signs can create a small window to respond gently, before things feel too distant to reach.",
            },
            {
              type: "list",
              title: "Common early signs people notice",
              items: [
                "A subtle sense of 'floating' or drifting away from the present moment",
                "Sounds becoming muffled or distant",
                "A feeling of pressure or fogginess behind the eyes",
                "Losing track of what was just said or done",
                "A sense of watching yourself rather than being in your body",
                "Emotions suddenly flattening or going quiet",
                "Feeling far away from people who are physically close by",
              ],
            },
            {
              type: "note",
              text: "You may recognise none, some, or many of these. That is simply information about you — not a diagnosis.",
            },
          ],
        },
        {
          slug: "noticing-my-patterns",
          kind: "reflect",
          title: "Noticing my patterns",
          summary: "Reflecting on when and where dissociation tends to show up.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "There is no need to have clear answers here — even a vague sense of a pattern is useful information.",
            },
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "When did I last notice myself feeling distant or disconnected?",
                "What was happening just before that feeling arrived?",
                "Does it tend to happen in particular places, relationships, or times of day?",
                "How do I usually find my way back afterwards?",
                "What would it mean to catch this a little earlier next time?",
              ],
            },
          ],
        },
        {
          slug: "relationships-intimacy-and-daily-life",
          kind: "learn",
          title: "Dissociation in relationships, intimacy and daily life",
          summary: "How disconnection can affect conversation, closeness, work and everyday tasks.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Dissociation can make it hard to follow conversation, make decisions, remember commitments, feel close to someone or communicate a boundary. During intimacy, signs of distance or reduced ability to respond are reasons to pause and restore clear choice.",
            },
            {
              type: "list",
              title: "Supportive responses can include",
              items: [
                "Using a pre-agreed pause word or gesture",
                "Reducing demands and offering simple choices",
                "Orienting to the room without forcing eye contact or touch",
                "Returning to decisions later when connection is clearer",
              ],
            },
            {
              type: "cta",
              text: "Explore consent-led communication and closeness at your pace.",
              label: "Trauma, Intimacy & Sex",
              to: "/learn/trauma-intimacy-sex",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise",
      blurb: "Gentle grounding and reorienting practices, entirely optional.",
      intro:
        "These practices are here if and when they feel useful. You are always allowed to stop, pause, or skip anything that doesn't feel right for you today.",
      resources: [
        {
          slug: "grounding-and-reorienting",
          kind: "practice",
          title: "Grounding and reorienting",
          summary: "A gentle practice for reconnecting with the present moment.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "Grounding works by gently drawing attention back to the present — your body, your surroundings, this moment — rather than trying to force feelings away. Go at whatever pace feels manageable, and stop at any point if it feels like too much.",
            },
            {
              type: "steps",
              title: "A simple grounding sequence",
              items: [
                { label: "Notice your feet", note: "Feel them pressing into the floor, or your shoes around them." },
                { label: "Name five things you can see", note: "Say them silently or out loud, slowly." },
                { label: "Name four things you can hear", note: "Let yourself really listen for a moment." },
                { label: "Name three things you can touch", note: "The texture of your clothes, a chair, the air." },
                { label: "Say today's date and where you are", note: "Out loud if possible — this helps orient the mind to now." },
                { label: "Take one slower breath", note: "No need to control it perfectly, just let it be a little longer." },
              ],
            },
            {
              type: "note",
              text: "If grounding doesn't work straight away, that's alright — sometimes it takes several attempts, or a different approach on different days. You might also find our Grounding and Breathing tools useful alongside this.",
            },
          ],
        },
        {
          slug: "reconnecting-with-your-body",
          kind: "practice",
          title: "Reconnecting with your body, gently",
          summary: "Small, low-pressure ways to come back into physical awareness.",
          minutes: 5,
          blocks: [
            {
              type: "list",
              title: "Ideas to try, one at a time",
              items: [
                "Hold something cold or textured, like an ice cube or a smooth stone",
                "Press your palms together firmly and notice the sensation",
                "Name the colour and texture of three things in the room",
                "Stand up and gently shift your weight from foot to foot",
                "Splash cool water on your hands or wrists",
                "Say your own name and a simple, true fact out loud",
              ],
            },
            {
              type: "note",
              text: "There is no 'correct' way to do this. Some strategies will suit you more than others — it can help to build a small personal list of what tends to work.",
            },
          ],
        },
        {
          slug: "when-to-seek-support",
          kind: "learn",
          title: "When to seek professional or medical support",
          summary: "Frequent, risky or disruptive dissociation deserves careful assessment.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Consider speaking with a GP or qualified mental health professional if disconnection is frequent, distressing, affects work or relationships, creates safety concerns, or involves significant memory gaps. New confusion, fainting, seizures, head injury or other physical changes may need prompt medical assessment.",
            },
            {
              type: "note",
              text: "Seek urgent help if you are in immediate danger, cannot keep yourself safe, or become disoriented in a situation such as driving. You deserve support before things reach crisis point.",
            },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Printable worksheets for building your own understanding.",
      intro:
        "These worksheets are for your own use — private, and entirely at your own pace. Consider sharing them with a trusted person or therapist if that feels right.",
      resources: [
        {
          slug: "my-dissociation-map",
          kind: "worksheet",
          title: "My dissociation map",
          summary: "A printable worksheet for tracking your own early signs and what helps.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "This worksheet is designed to help you build a personal, practical understanding of your own patterns — not to diagnose or label them.",
            },
            {
              type: "note",
              text: "This is educational and reflective, not a clinical assessment. If dissociation is affecting your daily life, please also consider speaking with a doctor or trauma-informed therapist.",
            },
          ],
          worksheetFields: [
            { label: "My early signs of dissociation", hint: "What do you notice first — in your body, thoughts or mood?", lines: 4 },
            { label: "What usually precedes it", hint: "Situations, people, times of day, or feelings that often come just before.", lines: 4 },
            { label: "What helps me reconnect", hint: "Grounding techniques, sensations, places or people that help you come back.", lines: 4 },
            { label: "Who I can tell", hint: "Someone you trust who could support you if this happens around them.", lines: 3 },
          ],
        },
        {
          slug: "my-reconnection-plan",
          kind: "worksheet",
          title: "My early-warning and reconnection plan",
          summary: "A private plan for noticing distance and returning to the present safely.",
          minutes: 10,
          blocks: [
            {
              type: "note",
              text: "Choose external anchors first if focusing inside your body increases disconnection. This plan is not a substitute for professional support or an emergency plan.",
            },
            {
              type: "cta",
              text: "Try adaptable, external-first present-moment practices.",
              label: "Open Grounding",
              to: "/grounding",
            },
          ],
          worksheetFields: [
            { label: "My earliest warning signs", lines: 4 },
            { label: "Present-day facts that help orient me", lines: 3 },
            { label: "External grounding choices I prefer", lines: 4 },
            { label: "What I want another person to do or avoid", lines: 4 },
            { label: "When and where I will seek professional help", lines: 3 },
          ],
        },
      ],
    },
  ],
  continueTo: [
    { label: "Try a grounding exercise", to: "/grounding" },
    { label: "Window of Tolerance", to: "/learn/window-of-tolerance" },
    { label: "Polyvagal Ladder", to: "/learn/polyvagal-ladder" },
    { label: "Understanding C-PTSD", to: "/learn/cptsd" },
    { label: "Crisis resources", to: "/crisis" },
    { label: "Trauma & the Nervous System", to: "/learn/trauma-nervous-system" },
    { label: "Trauma, Intimacy & Sex", to: "/learn/trauma-intimacy-sex" },
  ],
};
